import React, { useCallback, useEffect, useState } from "react";
import { Cloud, CheckCircle2, XCircle, RefreshCw, PlayCircle, ExternalLink } from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// V1.24.1 · Panel de validación Microsoft Graph / OneDrive.
// Muestra estado de configuración y permite ejecutar el smoke-test end-to-end
// sin necesidad de curl ni exponer credenciales de administración.

const StepRow = ({ step, ok, extra }) => (
  <div
    className={
      "flex items-start gap-2 rounded-lg px-3 py-2 border " +
      (ok
        ? "bg-emerald-50 border-emerald-200"
        : "bg-red-50 border-red-200")
    }
    data-testid={`onedrive-step-${step}`}
  >
    {ok ? (
      <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
    )}
    <div className="flex-1 min-w-0">
      <div className="text-xs font-bold uppercase tracking-wider text-neutral-800">
        {step}
      </div>
      {extra && (
        <pre className="text-[11px] text-neutral-700 whitespace-pre-wrap break-all mt-0.5 font-mono">
          {extra}
        </pre>
      )}
    </div>
  </div>
);

export default function OneDrivePanel() {
  const pin = usePinSecurity();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      await pin.require("onedrive.read");
      const { data } = await api.get("/onedrive/status");
      setStatus(data);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      setError(msg);
      if (!/cancel/i.test(msg)) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runTest = async () => {
    setError("");
    setResult(null);
    setRunning(true);
    try {
      await pin.require("onedrive.write");
      const { data } = await api.post("/onedrive/test-connection");
      setResult(data);
      if (data?.ok) toast.success("Test OneDrive OK — 5/5 pasos");
      else toast.error("Test OneDrive falló en algún paso");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  const configured = !!status?.configured;

  return (
    <div className="card-surface p-4 space-y-4" data-testid="onedrive-panel">
      {/* Cabecera */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Cloud className="h-5 w-5 text-orange-500" />
          <h3 className="font-bold">OneDrive · Microsoft Graph</h3>
          <span className="text-[10px] font-mono text-neutral-400">V1.24.1</span>
        </div>
        <button
          onClick={loadStatus}
          disabled={loading}
          className="btn-secondary h-8 px-2 text-xs"
          data-testid="onedrive-refresh"
        >
          <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
          Refrescar
        </button>
      </div>

      <p className="text-xs text-neutral-500">
        Repositorio documental corporativo. Los binarios (PDF, imágenes) se
        almacenan en OneDrive Empresarial; MongoDB solo guarda metadatos y
        referencias.
      </p>

      {/* Estado */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="text-[10px] uppercase text-neutral-500 font-semibold">Estado</div>
          <div
            className={"mt-0.5 font-bold " + (configured ? "text-emerald-700" : "text-red-700")}
            data-testid="onedrive-status-configured"
          >
            {configured ? "Configurado ✓" : "No configurado"}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="text-[10px] uppercase text-neutral-500 font-semibold">Provider</div>
          <div className="mt-0.5 font-mono text-neutral-800" data-testid="onedrive-provider">
            {status?.provider || "—"}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="text-[10px] uppercase text-neutral-500 font-semibold">Destino</div>
          <div className="mt-0.5 font-mono text-neutral-800 truncate" title={status?.drive_target} data-testid="onedrive-drive-target">
            {status?.drive_target || "—"}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-2">
          <div className="text-[10px] uppercase text-neutral-500 font-semibold">Carpeta raíz</div>
          <div className="mt-0.5 font-mono text-neutral-800 truncate" title={status?.root_folder} data-testid="onedrive-root-folder">
            {status?.root_folder || "—"}
          </div>
        </div>
      </div>

      {status?.tenant_id_mask && (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-1 text-[11px] font-mono" data-testid="onedrive-ids-diag">
          <div className="text-[10px] font-sans uppercase text-neutral-500 font-semibold tracking-wider">
            Diagnóstico Azure (AADSTS)
          </div>
          <div>Tenant ID: <span className="text-neutral-800" data-testid="onedrive-tenant-mask">{status.tenant_id_mask}</span></div>
          <div>Client ID: <span className="text-neutral-800" data-testid="onedrive-client-mask">{status.client_id_mask || "—"}</span></div>
          <div>Client Secret: <span className={status?.secret_present ? "text-emerald-700" : "text-red-700"} data-testid="onedrive-secret-present">
            {status?.secret_present ? `presente (${status.secret_length} chars)` : "AUSENTE"}
          </span></div>
          <div className="text-[10px] font-sans text-neutral-500 pt-1">
            Compara ambos GUIDs con Azure Portal → App Registrations → Overview.
            Formato mostrado: <span className="font-mono">8 primeros … 4 últimos</span>.
          </div>
        </div>
      )}

      {/* Advertencia si no configurado */}
      {!configured && !loading && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="onedrive-not-configured">
          <b>Credenciales no cargadas en este entorno.</b> Añade los secretos
          {" "}<code>MS_GRAPH_TENANT_ID</code>, <code>MS_GRAPH_CLIENT_ID</code>,
          {" "}<code>MS_GRAPH_CLIENT_SECRET</code>, <code>MS_GRAPH_DRIVE_USER</code>
          {" "}en el panel de configuración del hosting y redespliega la aplicación.
        </div>
      )}

      {/* Test de conexión */}
      <div className="rounded-lg border-2 border-orange-200 bg-orange-50/40 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="font-bold text-sm">Smoke test end-to-end</div>
            <p className="text-[11px] text-neutral-600">
              Crea <span className="font-mono">PRUEBA_ONEDRIVE</span>, sube{" "}
              <span className="font-mono">test.pdf</span> (21 bytes), lo lista y lo elimina.
              La carpeta se conserva vacía. No toca datos de alumnos.
            </p>
          </div>
          <button
            onClick={runTest}
            disabled={running || !configured}
            className="btn-primary h-9 px-3"
            data-testid="onedrive-run-test"
            title={!configured ? "Configura credenciales primero" : ""}
          >
            <PlayCircle className={"h-4 w-4 " + (running ? "animate-pulse" : "")} />
            {running ? "Ejecutando…" : "Ejecutar test"}
          </button>
        </div>

        {/* Resultado */}
        {result && (
          <div className="mt-2 space-y-1.5" data-testid="onedrive-test-result">
            <div className={"text-xs font-bold " + (result.ok ? "text-emerald-700" : "text-red-700")}>
              {result.ok ? "✓ 5/5 pasos verdes" : "✗ Falló algún paso"}
            </div>
            {(result.steps || []).map((s, i) => {
              const extra = Object.entries(s)
                .filter(([k]) => k !== "step" && k !== "ok")
                .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
                .join("\n");
              return <StepRow key={i} step={s.step} ok={!!s.ok} extra={extra} />;
            })}
            {/* Link directo al PDF si sale antes de borrar */}
            {result.steps?.find((s) => s.step === "upload" && s.web_url) && (
              <a
                href={result.steps.find((s) => s.step === "upload").web_url}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-orange-700 hover:underline"
                data-testid="onedrive-web-url"
              >
                <ExternalLink className="h-3 w-3" /> Ver en OneDrive (link creado durante el test)
              </a>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-800 whitespace-pre-wrap" data-testid="onedrive-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
